import { Badge } from '@/components/ui'

import { cn } from '@/lib/utils'
import { PosModel } from '@/types'
import { daysUntil, formatMoney, formatQty } from '@/utils'

interface ProductCardProps {
  product: PosModel.Product
  inCart?: number
  onAdd: (product: PosModel.Product) => void
}

/** Expiry shows on the tile once it is this close; FEFO sells the soonest batch first. */
const EXPIRY_SOON_DAYS = 30

/** A compact text tile (design "Register" screen): name, code, badges, price and what is left. */
export const ProductCard = ({ product, inCart, onAdd }: ProductCardProps) => {
  // Still sellable at zero: the sale goes below stock.
  const out = product.stock <= 0
  const low = !out && product.stock <= product.low_stock_threshold
  const variable = product.price_type === 'variable'
  const expiresIn = product.expiry_date ? daysUntil(product.expiry_date) : null
  const code = product.barcodes[0] ?? product.sku
  const left = product.sold_by_unit ? `${formatQty(product.stock)} ${product.unit}` : `${formatQty(product.stock)} left`

  return (
    <button
      type="button"
      onClick={() => onAdd(product)}
      className={cn(
        'relative flex min-h-24 flex-col gap-0.5 rounded-sm border border-border bg-card p-2.5 text-left shadow-z1 outline-none transition-[box-shadow,transform] hover:shadow-z8 focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98]',
        out && 'opacity-60',
        inCart && 'ring-2 ring-primary'
      )}
    >
      {!!inCart && (
        <span className="absolute right-2 top-2 grid h-5 min-w-5 place-items-center rounded-full bg-primary px-1 text-xs font-semibold text-white">
          {formatQty(inCart)}
        </span>
      )}
      <p className={cn('line-clamp-2 text-sm font-semibold leading-5 text-ink', inCart && 'pr-7')}>{product.name}</p>
      {code && <p className="truncate font-mono text-xs text-muted-foreground">{code}</p>}
      {(product.sold_by_unit || variable || low || (expiresIn !== null && expiresIn <= EXPIRY_SOON_DAYS)) && (
        <div className="flex flex-wrap gap-1">
          {product.sold_by_unit && <Badge className="rounded-xs" tone="info">Per {product.unit}</Badge>}
          {variable && <Badge className="rounded-xs" tone="info">Variable</Badge>}
          {expiresIn !== null && expiresIn <= EXPIRY_SOON_DAYS && (
            <Badge className="rounded-xs" tone={expiresIn <= 7 ? 'error' : 'warning'}>{expiresIn < 0 ? 'Expired' : `Exp ${expiresIn} d`}</Badge>
          )}
          {low && <Badge className="rounded-xs" tone="error">Low</Badge>}
        </div>
      )}
      <div className="mt-auto flex flex-wrap items-baseline justify-between gap-x-2 pt-1">
        <span className="flex items-baseline gap-1.5">
          <span className="text-heading-md tabular-nums text-ink">{variable && !product.price ? '৳ —' : formatMoney(product.price)}</span>
          {product.compare_price && product.compare_price > product.price && (
            <span className="text-xs tabular-nums text-faint line-through">{formatMoney(product.compare_price)}</span>
          )}
        </span>
        <span className={cn('shrink-0 text-xs tabular-nums', out ? 'text-error' : 'text-muted-foreground')}>{out ? 'out of stock' : left}</span>
      </div>
    </button>
  )
}
